import { isPlatformBrowser } from '@angular/common';
import { Component, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NavbarComponent } from './shared/ui/navbar/navbar.component';
import { SidebarComponent } from './shared/ui/sidebar/sidebar.component';
import { LoadingService } from './core/loading/loading.service';
import { LayoutService } from './core/layout/layout.service';
import { filter, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule, ProgressSpinner, NavbarComponent, SidebarComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent {
  title = 'legal-office-frontend';
  showNavbar = true;
  showSidebar = true;
  private readonly minimalShellRoutes = ['/login', '/register', '/legal/terms', '/legal/privacy'];
  private readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly loadingService = inject(LoadingService);

  constructor() {
    this.layoutService.sidebarVisible$
      .pipe(takeUntilDestroyed())
      .subscribe(visible => this.showSidebar = visible);

    this.updateLayoutVisibility(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => this.updateLayoutVisibility(event.urlAfterRedirects));

    if (isPlatformBrowser(this.platformId)) {
      fromEvent(window, 'resize')
        .pipe(debounceTime(100), takeUntilDestroyed())
        .subscribe(() => this.updateLayoutVisibility(this.router.url));
    }
  }

  private updateLayoutVisibility(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const minimalShell = this.minimalShellRoutes.includes(path);
    this.showNavbar = !minimalShell;
    if (minimalShell) {
      this.layoutService.setSidebarOpen(false);
    } else {
      this.layoutService.setSidebarOpen(!this.layoutService.isMobile());
    }
  }
}
